'use client';

import React, { useState, useEffect } from 'react';

const INDUSTRIES = [
  'Healthcare',
  'Banking & Financial Services',
  'Manufacturing',
  'Retail',
  'Insurance',
  'Government',
  'Education',
  'Energy & Utilities',
  'Telecommunications',
];

const ORG_SIZES = [
  { value: 'lt500', label: '< 500 employees' },
  { value: '500-2000', label: '500 – 2,000 employees' },
  { value: '2000-10000', label: '2,000 – 10,000 employees' },
  { value: 'gt10000', label: '10,000+ employees' },
];

export interface BusinessProfile {
  industry: string;
  orgSize: string;
  appPortfolioSize: number;
}

interface Props {
  onSave: (profile: BusinessProfile) => void;
  onDismiss: () => void;
}

export default function BusinessProfileModal({ onSave, onDismiss }: Props) {
  const [industry, setIndustry] = useState('');
  const [orgSize, setOrgSize] = useState('');
  const [appCount, setAppCount] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Animate in
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  const handleSave = () => {
    if (!industry || !orgSize) return;
    const profile: BusinessProfile = {
      industry,
      orgSize,
      appPortfolioSize: parseInt(appCount) || 0,
    };
    onSave(profile);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300"
      style={{
        background: 'rgba(13,27,42,0.85)',
        opacity: visible ? 1 : 0,
      }}
    >
      <div
        className="w-full max-w-lg rounded-xl p-6 transition-all duration-300"
        style={{
          background: '#111F30',
          border: '1px solid rgba(255,255,255,0.12)',
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(12px)',
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full" style={{ background: '#4ECDC4' }} />
              <span className="text-xs font-semibold tracking-widest" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
                OPTIONAL
              </span>
            </div>
            <h2 className="text-lg font-bold text-white">Business Profile</h2>
            <p className="text-sm mt-1" style={{ color: '#A8B2C1' }}>
              Personalizes defaults in Discover and Assess. Stored locally in your browser only.
            </p>
          </div>
          <button
            onClick={onDismiss}
            className="p-1.5 rounded transition-colors ml-4 flex-shrink-0"
            style={{ color: '#A8B2C1' }}
            aria-label="Dismiss business profile modal"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Fields */}
        <div className="flex flex-col gap-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5 tracking-wide" style={{ color: '#A8B2C1' }}>
              Industry
            </label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="input-base"
              style={{ background: '#1A2E42' }}
            >
              <option value="">Select industry...</option>
              {INDUSTRIES.map((ind) => (
                <option key={`ind-opt-${ind}`} value={ind}>{ind}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5 tracking-wide" style={{ color: '#A8B2C1' }}>
              Organization Size
            </label>
            <select
              value={orgSize}
              onChange={(e) => setOrgSize(e.target.value)}
              className="input-base"
              style={{ background: '#1A2E42' }}
            >
              <option value="">Select size...</option>
              {ORG_SIZES.map((s) => (
                <option key={`size-opt-${s.value}`} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5 tracking-wide" style={{ color: '#A8B2C1' }}>
              Application Portfolio Size <span style={{ color: '#A8B2C1', fontWeight: 400 }}>(approximate)</span>
            </label>
            <input
              type="number"
              value={appCount}
              onChange={(e) => setAppCount(e.target.value)}
              placeholder="e.g. 150"
              className="input-base"
              min="1"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-6">
          <button
            onClick={handleSave}
            disabled={!industry || !orgSize}
            className="btn-primary flex-1"
          >
            Save Profile
          </button>
          <button
            onClick={onDismiss}
            className="btn-ghost"
          >
            Skip for now
          </button>
        </div>

        <p className="text-xs mt-3 text-center" style={{ color: '#A8B2C1' }}>
          No data is transmitted. Profile is stored in your browser only.
        </p>
      </div>
    </div>
  );
}